import { formatPlainTextAsHtmlEmail, looksLikeHtmlEmail } from '@/lib/textTemplateVariables'

const EMAIL_INLINE_STYLES = {
  p: 'margin:0 0 1em;line-height:1.6;',
  ul: 'margin:0 0 1em;padding-left:1.5em;list-style-type:disc;',
  ol: 'margin:0 0 1em;padding-left:1.5em;list-style-type:decimal;',
  li: 'margin:0 0 0.35em;line-height:1.5;',
  blockquote: 'margin:1em 0;padding-left:1em;border-left:4px solid #cbd5e1;color:#475569;',
  strong: 'font-weight:700;',
  b: 'font-weight:700;',
  em: 'font-style:italic;',
  i: 'font-style:italic;',
  u: 'text-decoration:underline;',
  s: 'text-decoration:line-through;',
  strike: 'text-decoration:line-through;',
  del: 'text-decoration:line-through;',
  a: 'color:#2563eb;text-decoration:underline;',
  h1: 'margin:0 0 0.75em;font-size:1.5em;font-weight:700;line-height:1.3;',
  h2: 'margin:0 0 0.75em;font-size:1.25em;font-weight:700;line-height:1.3;',
  h3: 'margin:0 0 0.75em;font-size:1.1em;font-weight:700;line-height:1.3;',
  pre: 'margin:0 0 1em;padding:0.75em;background:#f1f5f9;border-radius:4px;overflow-x:auto;font-family:ui-monospace,monospace;font-size:0.9em;',
  code: 'font-family:ui-monospace,monospace;background:#f1f5f9;padding:0.1em 0.35em;border-radius:3px;font-size:0.9em;',
}

/** Preview-only styles: layout without hard-coded colors (dark mode handled via CSS). */
const PREVIEW_INLINE_STYLES = {
  p: 'margin:0 0 1em;line-height:1.6;',
  ul: 'margin:0 0 1em;padding-left:1.5em;list-style-type:disc;',
  ol: 'margin:0 0 1em;padding-left:1.5em;list-style-type:decimal;',
  li: 'margin:0 0 0.35em;line-height:1.5;',
  blockquote: 'margin:1em 0;padding-left:1em;border-left:4px solid #cbd5e1;',
  strong: 'font-weight:700;',
  b: 'font-weight:700;',
  em: 'font-style:italic;',
  i: 'font-style:italic;',
  u: 'text-decoration:underline;',
  s: 'text-decoration:line-through;',
  strike: 'text-decoration:line-through;',
  del: 'text-decoration:line-through;',
  a: 'text-decoration:underline;',
  h1: 'margin:0 0 0.75em;font-size:1.5em;font-weight:700;line-height:1.3;',
  h2: 'margin:0 0 0.75em;font-size:1.25em;font-weight:700;line-height:1.3;',
  h3: 'margin:0 0 0.75em;font-size:1.1em;font-weight:700;line-height:1.3;',
  pre: 'margin:0 0 1em;padding:0.75em;border-radius:4px;overflow-x:auto;font-family:ui-monospace,monospace;font-size:0.9em;',
  code: 'font-family:ui-monospace,monospace;padding:0.1em 0.35em;border-radius:3px;font-size:0.9em;',
}

function parseHtmlFragment(html) {
  const doc = new DOMParser().parseFromString(`<div id="email-root">${html}</div>`, 'text/html')
  return doc.getElementById('email-root')
}

function mergeStyle(el, style) {
  const existing = el.getAttribute('style') || ''
  if (!existing.includes(style.split(':')[0])) {
    el.setAttribute('style', `${existing}${style}`)
  }
}

function applyInlineEmailStyles(root, styleMap = EMAIL_INLINE_STYLES) {
  if (!root) return

  Object.entries(styleMap).forEach(([tag, style]) => {
    root.querySelectorAll(tag).forEach((el) => mergeStyle(el, style))
  })

  root.querySelectorAll('li').forEach((li) => {
    Array.from(li.classList).forEach((className) => {
      if (className.startsWith('ql-indent-')) {
        const level = Number.parseInt(className.replace('ql-indent-', ''), 10) || 0
        if (level > 0) {
          mergeStyle(li, `margin-left:${level * 1.5}em;`)
        }
        li.classList.remove(className)
      }
    })
    li.querySelectorAll('.ql-ui').forEach((ui) => ui.remove())
    li.removeAttribute('data-list')
  })

  root.querySelectorAll('[class]').forEach((el) => {
    if (el.className === '' || el.classList.length === 0) {
      el.removeAttribute('class')
    }
  })
}

/**
 * Convert Quill 2 list markup (ol + data-list) into semantic ul/ol for email clients.
 */
export function normalizeQuillHtmlForEmail(html, { forPreview = false } = {}) {
  if (!html || !String(html).trim()) {
    return ''
  }

  const root = parseHtmlFragment(html)
  if (!root) {
    return html
  }

  const quillLists = Array.from(root.querySelectorAll('ol'))
  quillLists.forEach((ol) => {
    const items = Array.from(ol.children).filter((child) => child.tagName === 'LI')
    if (items.length === 0) {
      ol.remove()
      return
    }

    const fragment = root.ownerDocument.createDocumentFragment()
    let currentList = null
    let currentType = null

    items.forEach((li) => {
      const dataList = li.getAttribute('data-list')
      const listType = dataList === 'ordered' || dataList === 'checked' || dataList === 'unchecked'
        ? 'ol'
        : 'ul'

      if (!currentList || currentType !== listType) {
        currentList = root.ownerDocument.createElement(listType)
        fragment.appendChild(currentList)
        currentType = listType
      }

      currentList.appendChild(li)
    })

    ol.replaceWith(fragment)
  })

  applyInlineEmailStyles(root, forPreview ? PREVIEW_INLINE_STYLES : EMAIL_INLINE_STYLES)
  return root.innerHTML
}

export function prepareEmailHtmlForDisplay(content) {
  if (!content || !String(content).trim()) {
    return ''
  }

  const text = String(content)
  const html = looksLikeHtmlEmail(text) ? text : formatPlainTextAsHtmlEmail(text)
  const normalized = normalizeQuillHtmlForEmail(html, { forPreview: true })

  return `<div class="email-html-body">${normalized}</div>`
}
